import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useQuery } from '@apollo/client';

import { ActionButton, ButtonModel } from '@notino/react-styleguide';
import {
  GetReviewSummaryQuery,
  GetReviewSummaryQueryVariables,
  GetReviewsQuery,
  GetReviewsQueryVariables,
} from '@notino/shared/definitions/types';

import productDetailMessages from '@containers/ProductDetailContainer/messages';
import { LoadingWrapper } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/LoadingWrapper';
import { ReviewsSkeleton } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/ReviewsSkeleton';
import { ReviewSummarySkeleton } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/ReviewSummarySkeleton';
import { messages } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/messages';
import { ReviewsItems } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems';
import {
  ReviewItemsProvider,
  useReviewItemsContext,
} from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/context';
import { SortBox } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/styled';
import { ReviewsSummary } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewsSummary';
import { ErrorMessage } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/styled';
import { CenterWrapper } from '@containers/ProductDetailContainer/styled';
import { withProviders } from '@context/contextConfig';

import { REVIEW_COUNT } from '../constants';

import getReviewsQuery from './queries/reviews.graphql';
import getReviewSummaryQuery from './queries/reviewSummary.graphql';
import { ReviewCount, ReviewsWrapper, TitleWrapper } from './styled';

interface IReviewsComponentProps {
  productCode: string;
  masterId: string;
  renderReviewsOnServer: boolean;
  isAllReviewsPage?: boolean;
}

export const ReviewsComponent: FC<IReviewsComponentProps> = ({
  masterId,
  productCode,
  isAllReviewsPage,
  renderReviewsOnServer,
}) => {
  const {
    state: { orderBy, orderDesc, page, hideTranslated },
  } = useReviewItemsContext();

  const {
    data: reviewsSummaryData,
    loading: reviewsSummaryLoading,
    error: reviewsSummaryError,
    refetch: reviewsSummaryRefetch,
  } = useQuery<GetReviewSummaryQuery, GetReviewSummaryQueryVariables>(
    getReviewSummaryQuery,
    {
      variables: {
        masterId,
      },
      ssr: renderReviewsOnServer,
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    data,
    error,
    loading,
    refetch: reviewsRefetch,
    fetchMore,
  } = useQuery<GetReviewsQuery, GetReviewsQueryVariables>(getReviewsQuery, {
    variables: {
      code: productCode,
      orderBy,
      orderDesc,
      page,
      pageSize: REVIEW_COUNT,
      hideTranslated,
    },
    partialRefetch: true,
    ssr: renderReviewsOnServer,
  });

  const reviewOverview =
    reviewsSummaryData?.productDetailByMasterId?.[0]?.reviewOverview;
  const reviewSummary =
    reviewsSummaryData?.productDetailByMasterId?.[0]?.reviewSummary;

  const textReviewsCount = reviewOverview?.textReviewsCount ?? 0;

  const reviewsLength = data?.reviews.length;

  const reviewCount = reviewsLength > 0 ? reviewsLength : '';

  return (
    <ReviewsWrapper>
      <TitleWrapper>
        <FormattedMessage
          {...productDetailMessages.reviewHeader}
          values={{ name: '' }}
        />
        <ReviewCount>{reviewCount}</ReviewCount>
      </TitleWrapper>

      <LoadingWrapper
        isLoading={reviewsSummaryLoading}
        isError={!reviewSummary || !!reviewsSummaryError}
        skeleton={<ReviewSummarySkeleton />}
        error={
          <CenterWrapper>
            <ErrorMessage>
              <FormattedMessage {...messages.reviewsSummaryFailed} />
            </ErrorMessage>

            <ActionButton
              onClick={() => reviewsSummaryRefetch()}
              buttonSize={ButtonModel.Sizes.xSmall}
            >
              <FormattedMessage {...messages.tryAgain} />
            </ActionButton>
          </CenterWrapper>
        }
      >
        <ReviewsSummary
          reviewSummary={reviewSummary}
          productCode={productCode}
          masterId={masterId}
          isAllReviewsPage={isAllReviewsPage}
        />
      </LoadingWrapper>

      <LoadingWrapper
        isLoading={loading}
        isError={!data || !!error}
        skeleton={<ReviewsSkeleton />}
        error={
          <>
            <SortBox />
            <CenterWrapper>
              <ErrorMessage>
                <FormattedMessage {...messages.reviewsFailed} />
              </ErrorMessage>
              <ActionButton
                onClick={() => reviewsRefetch()}
                buttonSize={ButtonModel.Sizes.xSmall}
              >
                <FormattedMessage {...messages.tryAgain} />
              </ActionButton>
            </CenterWrapper>
          </>
        }
      />

      <ReviewsItems
        productCode={productCode}
        reviews={data?.reviews || []}
        reviewsLoading={loading}
        reviewsError={error}
        refetch={reviewsRefetch}
        fetchMore={fetchMore}
        textReviewsCount={textReviewsCount}
        isAllReviewsPage={isAllReviewsPage}
      />
    </ReviewsWrapper>
  );
};

export const Reviews =
  withProviders<IReviewsComponentProps>(ReviewItemsProvider)(ReviewsComponent);
